import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { AxiosInstance } from 'axios';
import type { Logger } from '@wg/api-client';
import type { AuthCore } from '../auth/core';
import { normalizeUserData, type User, type UserData } from './types';

const USERS_API_URL = '/functions/v1/users';

export interface UserContextValue {
  user: User | null;
  setUser: (data: UserData | null) => void;
  isLoading: boolean;
  refreshUser: () => Promise<void>;
}

const UserContext = createContext<UserContextValue>({
  user: null,
  setUser: () => {},
  isLoading: false,
  refreshUser: async () => {},
});

export interface UserProviderProps {
  core: AuthCore;
  httpClient: AxiosInstance;
  logger: Logger;
  /** Auth state from `useAuthContext()` so we can drive refresh. */
  isAuthenticated: boolean;
  isGuest: boolean;
  children: React.ReactNode;
}

/**
 * Owns User state. Hydrates from JWT claims first (so UI doesn't blank during
 * /me flight), then fetches /me. On 401/403 in guest mode, recreates guest
 * session and retries once — mirrors RN useMe.ts behavior.
 */
export const UserProvider = ({
  core,
  httpClient,
  logger,
  isAuthenticated,
  isGuest,
  children,
}: UserProviderProps) => {
  const [user, setUserState] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const inFlight = useRef(false);

  const setUser = useCallback((data: UserData | null) => {
    setUserState(data ? normalizeUserData(data) : null);
  }, []);

  const refreshUser = useCallback(async () => {
    if (!isAuthenticated && !isGuest) {
      setUserState(null);
      return;
    }
    if (inFlight.current) return;

    const session = await core.getSessionInfo();
    if (!session.userId) {
      setUserState(null);
      return;
    }

    // JWT-claim hydration so the UI doesn't blank out while /me is in flight,
    // but only if we don't already have a fetched profile.
    setUserState(
      prev =>
        prev ?? {
          id: session.userId!,
          firstName: session.firstName ?? '',
          lastName: session.lastName ?? '',
          emailAddress: session.email ?? '',
          avatarUrl: session.avatarUrl ?? '',
        },
    );

    inFlight.current = true;
    setIsLoading(true);
    try {
      const { data } = await httpClient.get<{ user: UserData }>(
        `${USERS_API_URL}/me?t=${Date.now()}`,
      );
      if (data?.user?.id) setUserState(normalizeUserData(data.user));
    } catch (err: unknown) {
      const e = err as { response?: { status?: number } };
      const status = e?.response?.status;
      // RN useMe.ts guest-recovery: on 401/403 + guest, anonymous JWT may have
      // expired. Spin up a fresh guest session and retry /me once. Without
      // this, guests get bumped to /welcome whenever the token ages out.
      // The http client's own 401 retry already handles authed users via
      // tryRefreshAuthToken — guest path needs its own recovery.
      if ((status === 401 || status === 403) && isGuest) {
        try {
          await core.createGuestSession();
          const retry = await httpClient.get<{ user: UserData }>(
            `${USERS_API_URL}/me?t=${Date.now()}`,
          );
          if (retry.data?.user?.id) {
            setUserState(normalizeUserData(retry.data.user));
            return;
          }
        } catch {
          // fall through — keep JWT-claim hydration
        }
      }
      if (status !== 401 && status !== 403) {
        logger.warn('[user] Failed to fetch /me', err);
      }
    } finally {
      inFlight.current = false;
      setIsLoading(false);
    }
  }, [core, httpClient, logger, isAuthenticated, isGuest]);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  // Refetch when the auth core fires sessionUpdated (e.g. after signin/register
  // that promotes a guest to authenticated) so the new profile lands.
  useEffect(() => {
    return core.onAuthEvent(event => {
      if (event === 'sessionUpdated') {
        refreshUser();
      } else if (event === 'sessionCleared') {
        setUserState(null);
      }
    });
  }, [core, refreshUser]);

  const value = useMemo<UserContextValue>(
    () => ({ user, setUser, isLoading, refreshUser }),
    [user, setUser, isLoading, refreshUser],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUserContext = (): UserContextValue => useContext(UserContext);
